import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TenantAlertRequest } from '@entities/tenantSetting';
import { TagViewModel } from '../tenant-setting-tag/tenant-setting-tag.component';

const MAX_TAG_NOTIFY_MAIL_LIST_SIZE = 6;

/** 通知対象のViewModel */
export type NotificationViewModel = {
  name: string;
  keyword: string;
  checked: boolean;
};

/** テナントアラートのViewModel */
export type TenantAlertViewModel = {
  notifyKeywords: { [key: string]: boolean };
};

/** タグの通知先メールアドレスのViewModel */
export type TagNotifyEmailViewModel = {
  /** タグ名 */
  tagName: string;
  /** タグId */
  tagId: string;
  /** メールアドレス */
  notifyEmail?: string;
};

export type TagNotifyEmailListFormGroup = FormGroup<{
  tagEmailList: FormArray<TagNotifyEmailFormGroup>;
}>;

type TagNotifyEmailFormGroup = FormGroup<{
  tag: FormControl<string | null>;
  notifyEmail: FormControl<string | null>;
}>;

@Component({
  selector: 'app-tenant-setting-notification',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatTableModule,
    MatSortModule,
    MatTooltipModule,
    MatSelectModule,
    MatCheckboxModule,
  ],
  templateUrl: './tenant-setting-notification.component.html',
  styleUrl: './tenant-setting-notification.component.scss',
})
export class TenantSettingNotificationComponent
  implements OnChanges, AfterViewInit, OnInit
{
  /** テナント通知 */
  @Input() public tenantAlert: TenantAlertViewModel = {
    notifyKeywords: {},
  };

  /** 編集前のテナント通知 */
  @Input() public originalTenantAlert: TenantAlertViewModel = {
    notifyKeywords: {},
  };
  /** 送信先メールアドレス */
  @Input() public tagList: TagViewModel[] = [];
  /** ソート要素 */
  @ViewChild(MatSort) sort!: MatSort;
  /** 更新を通知 */
  @Output() updated = new EventEmitter<
    [TenantAlertViewModel, TagViewModel[]]
  >();

  /** カラム */
  public displayedColumns: string[] = ['tagName', 'notifyEmail'];

  /** 一覧情報 */
  public dataSource = new MatTableDataSource<TagNotifyEmailViewModel>([]);

  public tagNotifyEmailList: TagNotifyEmailViewModel[] = [];

  /** フォームグループ */
  public tagNotifyEmailListFormGroup: TagNotifyEmailListFormGroup;

  /** 編集フラグ */
  public updating: boolean = false;

  /** プルダウンの選択肢 */
  public originalTagList: TagViewModel[] = [];

  constructor(private el: ElementRef) {
    const tagNotifyEmailFormList: TagNotifyEmailFormGroup[] = [];
    for (let i = 0; i < MAX_TAG_NOTIFY_MAIL_LIST_SIZE; i++) {
      const formGroup: TagNotifyEmailFormGroup = new FormGroup(
          {
            tag: new FormControl('', {
              nonNullable: false,
            }),
            notifyEmail: new FormControl('', {
              nonNullable: false,
              validators: [
                this.notifyEmailListValidator(),
                Validators.maxLength(256),
              ],
            }),
          },
          { validators: this.tagEmailValidator() },
      );
      tagNotifyEmailFormList.push(formGroup);
    }

    this.tagNotifyEmailListFormGroup = new FormGroup({
      tagEmailList: new FormArray<TagNotifyEmailFormGroup>(
        tagNotifyEmailFormList,
      ),
    });
  }

  public sensors: NotificationViewModel[] = [
    { name: '温度センサー', keyword: 'temperatureSensor', checked: false },
    { name: '湿度センサー', keyword: 'humiditySensor', checked: false },
    { name: '気圧センサー', keyword: 'pressureSensor', checked: false },
    { name: '照度センサー', keyword: 'lightSensor', checked: false },
    { name: '傾斜センサー', keyword: 'tiltSensor', checked: false },
    { name: '落下検知', keyword: 'fallDetected', checked: false },
    { name: '動作検知', keyword: 'mcdDetected', checked: false },
    { name: '明暗検知', keyword: 'lightExposureDetected', checked: false },
    { name: '衝撃検知', keyword: 'shockDetected', checked: false },
  ];

  public deviceAlerts: NotificationViewModel[] = [
    { name: 'バッテリー低下', keyword: 'batteryLow', checked: false },
  ];

  public shipments: NotificationViewModel[] = [
    {
      name: '配送ステータス更新',
      keyword: 'shipmentStatusUpdate',
      checked: false,
    },
  ];

  public shipmentStatusUpdate: NotificationViewModel[] = [
    { name: '出発準備済', keyword: 'shipmentReady', checked: false },
    { name: '経由地到着', keyword: 'shipmentAtStop', checked: false },
    { name: '配送中', keyword: 'shipmentInProgress', checked: false },
    { name: '配送地到着', keyword: 'shipmentAtDestination', checked: false },
    { name: '完了', keyword: 'shipmentCompleted', checked: false },
    { name: '中断', keyword: 'shipmentInterrupted', checked: false },
  ];

  ngOnInit() {
    this.tagNotifyEmailList = this.createTagNotifyEmailViewModels(this.tagList);
    this.dataSource.data = this.tagNotifyEmailList;
    this.updateCheckboxes(this.tenantAlert.notifyKeywords);
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['tagList'] !== undefined) {
      const tagList: TagViewModel[] = changes['tagList'].currentValue;
      this.tagNotifyEmailList = this.createTagNotifyEmailViewModels(tagList);
      this.dataSource.data = this.tagNotifyEmailList;
    }

    if (changes['tenantAlert'] !== undefined) {
      this.updateCheckboxes(this.tenantAlert.notifyKeywords);
    }
  }

  /**
   * タグ名が選択されたときに紐づいた通知先メールアドレスを表示する
   *
   * @param selectedTagId 選択されたタグ名のタグID
   * @param element 通知先メールアドレスの要素
   */
  public onTagNameChange(
    selectedTagId: string,
    element: TagNotifyEmailViewModel,
  ): void {
    const index = this.tagNotifyEmailList.indexOf(element);
    const tagNotifyEmailListArray = this.tagNotifyEmailListFormGroup.get(
      'tagEmailList',
    ) as FormArray;
    const tagControl = tagNotifyEmailListArray.at(index).get('tag');
    const emailControl = tagNotifyEmailListArray.at(index).get('notifyEmail');

    if (selectedTagId === null) {
      element.notifyEmail = '';
      emailControl?.setValue(null);
      emailControl?.setErrors(null);
      tagControl?.setErrors(null);
    } else {
      const selectedTag = this.tagList.find(
        (tag) => tag.tagId === selectedTagId,
      );

      if (selectedTag) {
        element.notifyEmail = selectedTag.notifyEmail || '';
      } else {
        element.notifyEmail = '';
      }

      emailControl?.setValue(element.notifyEmail);
    }

    // バリデーションを再実行
    this.tagNotifyEmailListFormGroup.updateValueAndValidity();
  }

  /**
   * タグ名のみが空、もしくはメールアドレスのみが空のときのバリデーションを行う。
   * @returns タグ名とメールアドレスのバリデーションを行うバリデータ関数
   */
  public tagEmailValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const tagControl = control.get('tag');
      const emailControl = control.get('notifyEmail');

      if (tagControl?.value && !emailControl?.value) {
        // タグ名があり、メールアドレスが空の場合
        emailControl?.setErrors({ emailRequired: true });
        return { emailRequired: true };
      } else if (!tagControl?.value && emailControl?.value) {
        // メールアドレスがあり、タグ名が空の場合
        tagControl?.setErrors({ tagRequired: true });
        return { tagRequired: true };
      } else if (emailControl?.errors) {
        //emailの入力フォームには他のバリデーションがあるため。
        return emailControl.errors;
      }

      emailControl?.setErrors(null);
      tagControl?.setErrors(null);
      return null;
    };
  }

  /**
   * カンマ区切りで入力された通知先メールアドレスを検証する。
   *
   * @returns 通知先メールアドレスを検証するバリデータ関数
   */
  private notifyEmailListValidator(): ValidatorFn {
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value as string | null;
      if (!value) {
        return null;
      }

      const emailList = value.split(',').map((email) => email.trim());
      return emailList.some((email) => !email || !emailPattern.test(email))
        ? { emailList: true }
        : null;
    };
  }

  /**
   * 通知先メールアドレスをトリムし、重複を除外したカンマ区切り文字列へ変換する。
   *
   * @param notifyEmail 通知先メールアドレス
   * @returns 正規化した通知先メールアドレス
   */
  private normalizeNotifyEmails(notifyEmail: string): string {
    return [...new Set(notifyEmail.split(',').map((email) => email.trim()))].join(
      ',',
    );
  }

  /**
   * tagListからTagNotifyEmailViewModelの配列を作成し、空の行を追加する関数
   *
   * @param tagList タグのリスト
   * @returns 空の行を追加してソートされたTagNotifyEmailViewModel
   */
  private createTagNotifyEmailViewModels(
    tagList: TagViewModel[],
  ): TagNotifyEmailViewModel[] {
    const tagNotifyEmailList = tagList
      .map((tag) => ({
        tagName: tag.tagName,
        tagId: tag.tagId,
        notifyEmail: tag.notifyEmail,
      }))
      .filter(
        (tagNotifyEmail) =>
          tagNotifyEmail.notifyEmail !== null &&
          tagNotifyEmail.notifyEmail !== undefined &&
          tagNotifyEmail.notifyEmail !== '',
      );

    // 空の行を追加
    const emptyRowsNeeded =
      MAX_TAG_NOTIFY_MAIL_LIST_SIZE - tagNotifyEmailList.length;
    for (let i = 0; i < emptyRowsNeeded; i++) {
      tagNotifyEmailList.push({
        tagName: '',
        tagId: '',
        notifyEmail: '',
      });
    }
    return this.sortDataSource(tagNotifyEmailList);
  }

  /**
   * tagNotifyEmailListをもとにFormGroupに値をセットする。
   *
   * @param tagNotifyEmailList テーブルに表示しているTagNotifyEmailViewModelのリスト
   * @returns 空の行を追加してソートされたTagNotifyEmailViewModel
   */
  private setFormGroup(tagNotifyEmailList: TagNotifyEmailViewModel[]) {
    const formArray = this.tagNotifyEmailListFormGroup.get(
      'tagEmailList',
    ) as FormArray;
    tagNotifyEmailList.forEach((tagNotifyEmail, index) => {
      const formGroup = formArray?.at(index);
      formGroup.get('tag')?.setValue(tagNotifyEmail.tagId);
      formGroup.get('notifyEmail')?.setValue(tagNotifyEmail.notifyEmail);
    });
  }

  public ngAfterViewInit() {
    this.dataSource.sort = this.sort;
  }

  /**
   * 画面表示前に引数のリストをタグ名に応じて辞書順にソートする。
   *
   * @param data TagNotifyEmailViewModelかTagViewModelのリスト
   */
  public sortDataSource(
    data: TagNotifyEmailViewModel[] | TagViewModel[],
  ): TagNotifyEmailViewModel[] | TagViewModel[] {
    return data.sort((a, b) => {
      if (a.tagName === '' && b.tagName === '') {
        return 0;
      }
      if (a.tagName === '') {
        return 1;
      }
      if (b.tagName === '') {
        return -1;
      }
      return a.tagName.localeCompare(b.tagName);
    });
  }

  /**
   * 画面に表示するチェックボックスの値を更新する。
   *
   * @param targetNotifyKeywords テナント通知でチェックボックスがtrueとなる値の辞書
   */
  private updateCheckboxes(targetNotifyKeywords: {
    [key: string]: boolean;
  }): void {
    const notifyKeywords = targetNotifyKeywords;

    // 各チェックボックスを更新
    this.sensors.forEach((sensor) => {
      sensor.checked = notifyKeywords[sensor.keyword] || false;
    });

    // デバイスアラートのチェックボックスを更新
    this.deviceAlerts.forEach((deviceAlert) => {
      deviceAlert.checked = notifyKeywords[deviceAlert.keyword] || false;
    });

    // shipmentStatusUpdateのチェックボックスを更新
    this.shipmentStatusUpdate.forEach((status) => {
      status.checked = notifyKeywords[status.keyword] || false;
    });

    this.shipments.forEach((shipment) => {
      shipment.checked = notifyKeywords[shipment.keyword] || false;
    });

    // shipmentStatusUpdateとshipmentErrorの項目のチェックボックスがすべてチェックが入っているときに'配送ステータス'と'配送エラー'にチェックが入るようにする。
    const allStatusChecked = this.shipmentStatusUpdate.every(
      (status) => status.checked,
    );
    const shipmentStatusUpdateItem = this.shipments.find(
      (shipment) => shipment.keyword === 'shipmentStatusUpdate',
    );
    if (shipmentStatusUpdateItem) {
      shipmentStatusUpdateItem.checked = allStatusChecked;
    }
  }

  /** 編集モードの切り替え */
  public toggleUpdating(): void {
    this.updating = !this.updating;

    if (this.updating) {
      // 編集モードに入ったときの処理
      this.originalTagList = JSON.parse(JSON.stringify(this.dataSource.data));
      this.tagNotifyEmailList = this.createTagNotifyEmailViewModels(
        this.tagList,
      );
      this.setFormGroup(this.tagNotifyEmailList);
      this.dataSource.data = this.tagNotifyEmailList;
    } else {
      // 編集モードを終了する場合の処理（必要に応じて）
      this.dataSource.data = [...this.tagList];
    }
  }

  /** 更新処理 */
  public updateTenantAlert(): void {
    if (this.updating) {
      const notifyKeywords: { [key: string]: boolean } = {};

      // 各チェックボックスの状態を取得
      this.sensors.forEach((sensor) => {
        notifyKeywords[sensor.keyword] = sensor.checked;
      });
      this.deviceAlerts.forEach((deviceAlert) => {
        notifyKeywords[deviceAlert.keyword] = deviceAlert.checked;
      });
      this.shipments.forEach((shipment) => {
        notifyKeywords[shipment.keyword] = shipment.checked;
      });
      this.shipmentStatusUpdate.forEach((status) => {
        notifyKeywords[status.keyword] = status.checked;
      });

      // TenantAlertRequest形式に合わせてデータを作成
      const tenantAlertRequest: TenantAlertRequest = {
        notifyKeywords: notifyKeywords,
      };

      // 空のデータを削除して整理してtagRequestを作成
      const tagRequest: TagNotifyEmailViewModel[] = [];
      const tagNotifyEmailListArray = this.tagNotifyEmailListFormGroup.get(
        'tagEmailList',
      ) as FormArray;
      for (let index = 0; index < MAX_TAG_NOTIFY_MAIL_LIST_SIZE; index++) {
        const tagNotifyEmailListFormGroup = tagNotifyEmailListArray.at(index);
        const tagId = tagNotifyEmailListFormGroup.get('tag')?.value;
        const notifyEmail =
          tagNotifyEmailListFormGroup.get('notifyEmail')?.value;

        if (tagId && notifyEmail) {
          const tag = this.tagList.find((tag) => tag.tagId === tagId);
          tagRequest.push({
            tagName: tag?.tagName ?? '',
            tagId: tag?.tagId ?? '',
            notifyEmail: this.normalizeNotifyEmails(notifyEmail),
          });
        }
      }

      // updatedにリクエスト内容をemit
      this.updated.emit([tenantAlertRequest, tagRequest]);
      this.tagNotifyEmailList = this.createTagNotifyEmailViewModels(
        this.tagList,
      );
      this.dataSource.data = this.tagNotifyEmailList;
      this.updating = false;
    }
  }

  /**
   * 現在テーブル上で選択されているタグかどうかを判定する
   *
   * @param tagId タグID
   */
  public isTagSelected(tagId: string): boolean {
    const tagNotifyEmailListArray = this.tagNotifyEmailListFormGroup.get(
      'tagEmailList',
    ) as FormArray;
    return tagNotifyEmailListArray.controls.some(
      (control) => control.get('tag')?.value === tagId,
    );
  }

  /**
   * 編集モードでキャンセルボタンを押したときにデータを編集前に戻す。
   */
  public cancelEditing(): void {
    this.updating = false;
    this.dataSource.data = [...this.originalTagList];
    this.updateCheckboxes(this.originalTenantAlert.notifyKeywords);
  }

  /**
   * 指定された配列の中に、チェックボックスがtrueとなる項目があるかを判定する。
   * 動作検知は現在未実装のため判定の項目から除いている。
   * @param array NotificationViewModelの配列
   * @returns boolean
   */
  public hasValue(array: NotificationViewModel[]): boolean {
    return array.some((item) => item.checked && item.keyword !== 'mcdDetected');
  }

  /**
   * 指定された配列が、すべてチェックボックスがtrueの項目であるかを判定する。
   * 動作検知は現在未実装のため判定の項目から除いている。
   * @param array NotificationViewModelの配列
   * @returns boolean
   */
  public isAllSelected(array: NotificationViewModel[]): boolean {
    return array
      .filter((item) => item.keyword !== 'mcdDetected')
      .every((item) => item.checked);
  }

  /**
   * センサー情報の全選択/全選択解除を行う。
   * 動作検知は現在未実装のため判定の項目から除いている。
   */
  public toggleAllSensors() {
    const newValue = !this.isAllSelected(
      this.sensors.filter((sensor) => sensor.keyword !== 'mcdDetected'),
    );
    this.sensors.forEach((sensor) => {
      if (sensor.keyword !== 'mcdDetected') {
        sensor.checked = newValue;
      }
    });
  }

  /**
   * 配送情報の全選択/全選択解除を行う。
   */
  public toggleAllShipments() {
    const newValue = !this.isAllSelected(this.shipments);
    this.shipments.forEach((shipment) => {
      shipment.checked = newValue;

      // 配下のチェックボックスも更新
      if (shipment.keyword === 'shipmentStatusUpdate') {
        this.shipmentStatusUpdate.forEach(
          (status) => (status.checked = newValue),
        );
      }
    });
  }

  /**
   * 配送ステータス更新の全選択/全選択解除を行う。
   */
  public toggleAllShipmentStatusUpdate() {
    const newValue = !this.isAllSelected(this.shipmentStatusUpdate);
    this.shipmentStatusUpdate.forEach((status) => (status.checked = newValue));
  }

  /**
   * 指定されたインデックスのNotifyEmailFormGroupを取得する
   * @param index 要素番号（テーブルのindex行目に相当）
   * @returns  指定されたインデックスのNotifyEmailFormGroup
   */
  public getNotifyEmailFormGroup(index: number): TagNotifyEmailFormGroup {
    return this.tagNotifyEmailListFormGroup.controls.tagEmailList.controls[
      index
    ];
  }

  /**
   * 指定されたインデックスのNotifyEmailFormGroupに通知先メールアドレスの入力にエラーがあるかを判定します。
   *
   * @param index  要素番号（テーブルのindex行目に相当）
   * @returns エラーが存在する場合は true、それ以外は false
   */
  public hasNotifyEmailError(index: number): boolean {
    const notifyEmailControl =
      this.getNotifyEmailFormGroup(index).controls.notifyEmail;
    return notifyEmailControl.errors != null;
  }

  /**
   * 指定されたインデックスのNotifyEmailFormGroupのタグの入力にエラーがあるかを判定します。
   *
   * @param index  要素番号（テーブルのindex行目に相当）
   * @returns エラーが存在する場合は true、それ以外は false
   */
  public hasTagError(index: number): boolean {
    const notifyEmailControl = this.getNotifyEmailFormGroup(index).controls.tag;
    return notifyEmailControl.errors != null;
  }
}
